import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  createProjectFromTemplate,
  deleteProjectTemplate,
} from '../../pm/firebase';
import { linkify } from '../../pm/linkify';
import {
  ProjectPriority,
  ProjectTemplate,
  TemplateTask,
  TemplateWorkstream,
} from '../../pm/types';
import UseTemplateModal from './UseTemplateModal';

interface Props {
  uid: string;
  templates: ProjectTemplate[];
  templateWorkstreams: TemplateWorkstream[];
  templateTasks: TemplateTask[];
}

const TemplatesListView: React.FC<Props> = ({
  uid,
  templates,
  templateWorkstreams,
  templateTasks,
}) => {
  const history = useHistory();
  const [useTarget, setUseTarget] = useState<ProjectTemplate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-compute per-template counts so each card lookup is O(1).
  const counts = useMemo(() => {
    const wsCount: Record<string, number> = {};
    const taskCount: Record<string, number> = {};
    for (const w of templateWorkstreams) {
      wsCount[w.templateId] = (wsCount[w.templateId] || 0) + 1;
    }
    for (const t of templateTasks) {
      taskCount[t.templateId] = (taskCount[t.templateId] || 0) + 1;
    }
    return { wsCount, taskCount };
  }, [templateWorkstreams, templateTasks]);

  async function handleUseSubmit(input: {
    title: string;
    description: string;
    priority?: ProjectPriority;
    startDate?: number;
  }) {
    if (!useTarget) return;
    setBusy(true);
    setError(null);
    try {
      const newProjectId = await createProjectFromTemplate(
        useTarget.id,
        input,
        templateWorkstreams,
        templateTasks,
        uid,
      );
      setUseTarget(null);
      history.push(`/Projects/${newProjectId}`);
    } catch (err: any) {
      setError(err?.message || 'Could not create project from template.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(template: ProjectTemplate) {
    const confirmed = window.confirm(
      `Delete template "${template.title}"?\nThis can't be undone.`,
    );
    if (!confirmed) return;
    try {
      await deleteProjectTemplate(template.id, templateWorkstreams, templateTasks);
    } catch (err: any) {
      // Surface inline so users aren't left guessing if delete didn't take.
      window.alert(err?.message || 'Could not delete template.');
    }
  }

  if (templates.length === 0) {
    return (
      <div className="pm-empty">
        <h3>No templates yet</h3>
        <p>
          Click <strong>New template</strong> above to author one from scratch, or open a project
          and use <strong>Save as template</strong> to capture its workstream + task layout.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="pm-templates-grid">
        {templates.map((t) => {
          const ws = counts.wsCount[t.id] || 0;
          const tasks = counts.taskCount[t.id] || 0;
          return (
            <div key={t.id} className="pm-template-card">
              <button
                type="button"
                className="pm-template-card-body"
                onClick={() => history.push(`/Projects/Templates/${t.id}`)}
              >
                <h3>{linkify(t.title, { stopPropagation: true })}</h3>
                <div className="pm-template-card-desc">
                  {t.description ? linkify(t.description, { stopPropagation: true }) : ' '}
                </div>
                <div className="pm-template-card-meta">
                  <span>{ws} workstream{ws === 1 ? '' : 's'}</span>
                  <span aria-hidden>·</span>
                  <span>{tasks} task{tasks === 1 ? '' : 's'}</span>
                </div>
              </button>
              <div className="pm-template-card-actions">
                <button
                  type="button"
                  className="pm-button pm-button-primary"
                  onClick={() => setUseTarget(t)}
                >
                  Use template
                </button>
                <button
                  type="button"
                  className="pm-link-button pm-danger"
                  onClick={() => handleDelete(t)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <UseTemplateModal
        open={!!useTarget}
        template={useTarget}
        workstreams={templateWorkstreams}
        tasks={templateTasks}
        busy={busy}
        error={error}
        onCancel={() => { if (!busy) { setUseTarget(null); setError(null); } }}
        onSubmit={handleUseSubmit}
      />
    </>
  );
};

export default TemplatesListView;
